import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon, ChevronDown, ArrowUpRight, Package, ShoppingBag } from "lucide-react";
import { NAV, CATEGORIES, BRAND } from "@/data/catalog";
import { useTheme } from "@/components/ThemeProvider";
import { useCart } from "@/context/CartContext";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mega, setMega] = useState(false);
  const [mobile, setMobile] = useState(false);
  const { theme, toggle } = useTheme();
  const { count, setOpen } = useCart();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobile(false);
    setMega(false);
  }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,border,padding] duration-300 ${
        scrolled ? "glass border-b border-border py-3" : "py-5 bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-navy text-white group-hover:bg-royal transition-colors">
            <Package size={18} strokeWidth={2.2} />
          </span>
          <span className="font-heading text-xl font-extrabold tracking-tight text-foreground">
            Elitz<span className="text-royal">Packs</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" onMouseLeave={() => setMega(false)}>
          {NAV.map((n) =>
            n.label === "Products" ? (
              <div key={n.label} onMouseEnter={() => setMega(true)} className="relative">
                <Link
                  to={n.to}
                  data-testid="nav-products"
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground/80 hover:text-foreground rounded-full transition-colors"
                >
                  Products <ChevronDown size={14} className={`transition-transform ${mega ? "rotate-180" : ""}`} />
                </Link>
              </div>
            ) : (
              <Link
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  loc.pathname === n.to ? "text-royal" : "text-foreground/80 hover:text-foreground"
                }`}
              >
                {n.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className="grid place-items-center h-10 w-10 rounded-full border border-border hover:bg-secondary transition-colors"
          >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <button
            data-testid="cart-button"
            onClick={() => setOpen(true)}
            aria-label="Open cart"
            className="relative grid place-items-center h-10 w-10 rounded-full border border-border hover:bg-secondary transition-colors"
          >
            <ShoppingBag size={17} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-emerald text-white text-[10px] font-bold">{count}</span>
            )}
          </button>
          <Link
            to="/request-quote"
            data-testid="header-quote-btn"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-navy text-white px-5 py-2.5 text-sm font-semibold hover:bg-royal hover:scale-[1.03] transition-all"
          >
            Get Quote <ArrowUpRight size={16} />
          </Link>
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden grid place-items-center h-10 w-10 rounded-full border border-border"
            onClick={() => setMobile((v) => !v)}
            aria-label="Menu"
          >
            {mobile ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mega menu */}
      <AnimatePresence>
        {mega && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            onMouseEnter={() => setMega(true)}
            onMouseLeave={() => setMega(false)}
            className="hidden lg:block absolute top-full inset-x-0 glass border-b border-border"
            data-testid="mega-menu"
          >
            <div className="mx-auto max-w-[1400px] px-10 py-8 grid grid-cols-4 gap-x-8 gap-y-3">
              {CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/products?category=${c.slug}`}
                  data-testid={`mega-${c.slug}`}
                  className="group flex flex-col gap-0.5 rounded-xl p-3 hover:bg-secondary transition-colors"
                >
                  <span className="overline text-royal">{c.tagline}</span>
                  <span className="font-heading font-bold text-foreground group-hover:text-royal transition-colors">{c.name}</span>
                  <span className="text-xs text-muted-foreground line-clamp-1">{c.short}</span>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobile && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-b border-border overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col">
              {NAV.map((n) => (
                <Link
                  key={n.label}
                  to={n.to}
                  className="py-3 border-b border-border/60 text-foreground font-medium"
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {n.label}
                </Link>
              ))}
              <Link to="/request-quote" className="mt-4 text-center rounded-full bg-navy text-white px-5 py-3 font-semibold">
                Get Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
